import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Save, Droplets, Zap, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const todayISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const ICONS = { water: Droplets, electricity: Zap };
const ACCENT = { water: "#2E6F7A", electricity: "#C86235" };

function MeterCard({ type, name, unit, rows, total, price, canEdit, onSavePrice }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(todayISO());
  const [value, setValue] = useState("");
  const [notes, setNotes] = useState("");
  const [priceDraft, setPriceDraft] = useState(price ?? "");
  useEffect(() => { setPriceDraft(price ?? ""); }, [price]);
  const Icon = ICONS[type] || Droplets;

  const save = useMutation({
    mutationFn: async () => (await api.post("/utilities/readings", { date, type, value: Number(value) || 0, notes })).data,
    onSuccess: () => { setValue(""); setNotes(""); qc.invalidateQueries({ queryKey: ["utility-readings"] }); toast.success("Η ένδειξη αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/utilities/readings/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["utility-readings"] }); toast.success("Διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid={`util-card-${type}`}>
      <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2.5 flex-wrap">
        <span className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${ACCENT[type]}1a` }}>
          <Icon className="h-5 w-5" style={{ color: ACCENT[type] }} />
        </span>
        <div><div className="font-medium text-[#45423C]">{name}</div><div className="text-xs text-[#8A8577]">Ένδειξη μετρητή ({unit})</div></div>
        <div className="ml-auto text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#8A8577]">Κόστος περιόδου</div>
          <div className="font-semibold tabular" style={{ color: ACCENT[type] }} data-testid={`util-total-${type}`}>{(total || 0).toLocaleString("el-GR", { minimumFractionDigits: 2 })} €</div>
        </div>
      </div>
      {canEdit && (
        <div className="px-4 py-3 border-b border-[#F1EDE6] bg-[#FAF8F4] flex items-end gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold">Τιμή ανά {unit} (€)</label>
            <input data-testid={`util-price-${type}`} type="number" step="0.001" value={priceDraft} onChange={(e) => setPriceDraft(e.target.value)}
              className="mt-1 w-32 rounded-lg border border-[#E3DED4] px-2 py-1.5 text-sm text-right tabular outline-none focus:border-[#45423C]" />
          </div>
          <button data-testid={`util-price-save-${type}`} onClick={() => onSavePrice(type, priceDraft)}
            className="rounded-lg bg-[#45423C] text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90">Αποθήκευση τιμής</button>
        </div>
      )}
      <div className="p-4 grid grid-cols-1 sm:grid-cols-[auto_1fr_1fr_auto] gap-2 items-end border-b border-[#F1EDE6]">
        <div><label className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold">Ημ/νία</label>
          <input data-testid={`util-date-${type}`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded-lg border border-[#E3DED4] px-2 py-2 text-sm outline-none focus:border-[#45423C]" /></div>
        <div><label className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold">Ένδειξη ({unit})</label>
          <input data-testid={`util-value-${type}`} type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} className="mt-1 w-full rounded-lg border border-[#E3DED4] px-2 py-2 text-sm text-right tabular outline-none focus:border-[#45423C]" /></div>
        <div><label className="text-[10px] uppercase tracking-wider text-[#6F6A60] font-semibold">Σημειώσεις</label>
          <input data-testid={`util-notes-${type}`} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="—" className="mt-1 w-full rounded-lg border border-[#E3DED4] px-2 py-2 text-sm outline-none focus:border-[#45423C]" /></div>
        <button data-testid={`util-save-${type}`} onClick={() => save.mutate()} disabled={value === "" || save.isPending}
          className="rounded-lg bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-1.5"><Save className="h-4 w-4" /> Save</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead><tr className="bg-[#F7F5F1] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
            <th className="px-4 py-2.5 font-semibold">Ημ/νία</th><th className="px-3 py-2.5 font-semibold text-right">Ένδειξη</th>
            <th className="px-3 py-2.5 font-semibold text-right">Κατανάλωση</th><th className="px-3 py-2.5 font-semibold text-right">Κόστος</th>
            <th className="px-3 py-2.5 font-semibold">Ώρα</th>{canEdit && <th className="px-2 py-2.5" />}</tr></thead>
          <tbody>
            {(rows || []).map((x) => (
              <tr key={x.id} className="border-t border-[#F1EDE6]">
                <td className="px-4 py-2 tabular text-[#6F6A60]">{x.date}</td>
                <td className="px-3 py-2 text-right tabular text-[#45423C]">{x.value}</td>
                <td className="px-3 py-2 text-right tabular font-semibold" style={{ color: ACCENT[type] }}>{x.consumption == null ? "–" : `${x.consumption} ${unit}`}</td>
                <td className="px-3 py-2 text-right tabular text-[#45423C]">{x.cost == null ? "–" : `${x.cost.toLocaleString("el-GR", { minimumFractionDigits: 2 })} €`}</td>
                <td className="px-3 py-2 text-xs text-[#8A8577] tabular">{x.updated_at ? fmtTime(x.updated_at) : "—"}</td>
                {canEdit && <td className="px-2 py-2 text-right"><button data-testid={`util-del-${x.id}`} onClick={() => del.mutate(x.id)} className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B]"><Trash2 className="h-3.5 w-3.5" /></button></td>}
              </tr>
            ))}
            {(rows || []).length === 0 && <tr><td colSpan={canEdit ? 6 : 5} className="px-4 py-8 text-center text-[#6F6A60]">Καμία ένδειξη ακόμη.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function MaintenanceUtilities() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr", "manager"].includes(user?.role);
  const { data: meta } = useQuery({ queryKey: ["utility-meta"], queryFn: async () => (await api.get("/utilities/meta")).data });
  const { data } = useQuery({ queryKey: ["utility-readings"], queryFn: async () => (await api.get("/utilities/readings")).data });
  const { data: cfg } = useQuery({ queryKey: ["utility-config"], queryFn: async () => (await api.get("/utilities/config")).data });

  const savePrice = useMutation({
    mutationFn: async ({ type, value }) => {
      const prices = { water: cfg?.prices?.water || 0, electricity: cfg?.prices?.electricity || 0, [type]: Number(value) || 0 };
      return (await api.put("/utilities/config", prices)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["utility-config"] }); qc.invalidateQueries({ queryKey: ["utility-readings"] }); toast.success("Η τιμή αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  return (
    <div className="space-y-6" data-testid="utilities-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#2E6F7A]">Συντήρηση · Παροχές</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Νερό & Ρεύμα</h1>
        <p className="text-[#6F6A60] text-sm mt-1">Εβδομαδιαία ένδειξη μετρητών — κατανάλωση & κόστος υπολογίζονται αυτόματα με βάση την τιμή ανά μονάδα.</p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {(meta?.types || []).map((t) => (
          <MeterCard key={t.id} type={t.id} name={t.name} unit={t.unit} rows={data?.readings?.[t.id]}
            total={data?.total_cost?.[t.id]} price={cfg?.prices?.[t.id]} canEdit={canEdit} onSavePrice={(type, value) => savePrice.mutate({ type, value })} />
        ))}
      </div>
    </div>
  );
}
